import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { X, Lock as LockIcon, CreditCard, RefreshCw, Flame } from 'lucide-react';
import { toast } from 'sonner';
import { getApiUrl } from '../../utils/api';
import { AlternatePhones } from './AlternatePhones';
import { CountrySignals } from './CountrySignals';
import { OwnershipHistory } from './OwnershipHistory';
import { EddHistory, eddSourceLabel } from './EddHistory';

const AI_ENGINE_LABELS = { gpt: 'ChatGPT', gemini: 'Gemini', perplexity: 'Perplexity', copilot: 'Copilot', claude: 'Claude', grok: 'Grok' };
import { TrialLmsStatus } from './TrialLmsStatus';
import { AddonStatus } from './AddonStatus';
import { LmsSlotHint } from './LmsSlotHint';
import { RenewalStatusField } from './RenewalStatusField';

const API_URL = getApiUrl();

const LeadModal = ({ lead, users, batches, currentUser, onClose, onSave, onRefresh }) => {
  const canEditFunnelFields = !lead || ['super_admin', 'pre_sales'].includes(currentUser?.role);
  const readOnly = !!lead && currentUser?.role === 'marketing';
  // Ownership: Super Admin moves sales + pre-sales; a Sales Team Leader moves sales only. Everyone else read-only.
  const isTL = currentUser?.role === 'sales' && currentUser?.is_team_lead;
  const canTransferSales = !lead || currentUser?.role === 'super_admin' || isTL;
  const canTransferPreSales = !lead || currentUser?.role === 'super_admin';

  // V36.6 — Always re-fetch the FULL lead document from /api/leads/{lead_id}
  // when the modal opens. Why: callers from dashboard endpoints (Sales /
  // Marketing) pass in a THIN projection (≤15 fields) — opening the editor
  // off that thin object made fields like pregnancy_notes / sales_notes /
  // due_date / trial_date / trial_batch_id / assigned_pre_sales_id look
  // empty even though they were filled in DB by the Pre-Sales rep. The
  // canonical /api/leads/{lead_id} endpoint returns the full Lead document
  // so admin and Pre-Sales views never drift.
  const [fullLead, setFullLead] = useState(lead);
  const [fullLeadLoading, setFullLeadLoading] = useState(!!lead?.lead_id);

  const refreshFullLead = async () => {
    if (!lead?.lead_id) return;
    try {
      const res = await axios.get(`${API_URL}/api/leads/${lead.lead_id}`, { withCredentials: true });
      setFullLead(res.data);
      if (res.data?.country) setFormData((prev) => ({ ...prev, country: res.data.country }));
    } catch (err) {
      console.warn('Could not re-fetch full lead, using thin object:', err?.response?.status);
    }
  };

  useEffect(() => {
    if (!lead?.lead_id) {
      setFullLeadLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/leads/${lead.lead_id}`, { withCredentials: true });
        if (!cancelled) setFullLead(res.data);
      } catch (err) {
        // Fall back to whatever was passed in — never crash on stale leads
        console.warn('Could not re-fetch full lead, using thin object:', err?.response?.status);
      } finally {
        if (!cancelled) setFullLeadLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [lead?.lead_id]);

  // V36.6 — Initialize form state lazily; once the full doc lands we replace it.
  // Using `fullLead` everywhere ensures unprojected fields populate when ready.
  const [formData, setFormData] = useState({
    full_name: lead?.full_name || '',
    // V32.2 — auto-heal existing leads opened for edit (strip hidden whitespace
    // that came in via copy-paste from WhatsApp/Excel). Without this, opening
    // an existing lead and re-saving fails browser email validation.
    email: (lead?.email || '').replace(/[\s\u00A0\u200B\u200C\u200D\uFEFF]+/g, '').toLowerCase(),
    phone: lead?.phone || '',
    city: lead?.city || '',
    country: lead?.country || 'India',
    timezone: lead?.timezone || '',
    funnel_type: lead?.funnel_type || '',
    funnel_sub_source: lead?.funnel_sub_source || '',
    referred_by_user_id: lead?.referred_by_user_id || '',
    referred_by_lead_id: lead?.referred_by_lead_id || '',
    doctor_id: lead?.doctor_id || '',
    assigned_pre_sales_id: lead?.assigned_pre_sales_id || '',
    assigned_sales_id: lead?.assigned_sales_id || lead?.assigned_sales_rep_id || '',
    lead_status: lead?.lead_status || 'new',
    callback_datetime: lead?.callback_datetime || '',
    trial_date: lead?.trial_date || '',
    trial_batch_id: lead?.trial_batch_id || '',
    trial_slot_id: lead?.trial_slot_id || '',
    trial_status: lead?.trial_status || 'pending',
    trial_payment_status: lead?.trial_payment_status || 'not_paid',
    consultation_status: lead?.consultation_status || 'pending',
    enrollment_status: lead?.enrollment_status || 'not_enrolled',
    pregnancy_notes: lead?.pregnancy_notes || '',
    sales_notes: lead?.sales_notes || '',
    lead_quality: lead?.lead_quality || 'not_rated',
    renewal_date: lead?.renewal_date || '',
    due_date: lead?.due_date || '',
  });

  // V36.6 — When the full lead lands, repopulate any field that the thin
  // object left blank. Never clobber edits the user has already made — only
  // fill fields that are still empty.
  useEffect(() => {
    if (!fullLead || fullLead === lead) return;
    setFormData((prev) => {
      const next = { ...prev };
      const fields = [
        'full_name', 'phone', 'city', 'country', 'timezone',
        'funnel_type', 'funnel_sub_source', 'referred_by_user_id',
        'referred_by_lead_id', 'doctor_id', 'assigned_pre_sales_id',
        'lead_status', 'callback_datetime', 'trial_date',
        'trial_batch_id', 'trial_slot_id', 'trial_status',
        'trial_payment_status', 'consultation_status', 'enrollment_status',
        'pregnancy_notes', 'sales_notes', 'lead_quality',
        'renewal_date', 'due_date',
      ];
      for (const f of fields) {
        if ((next[f] === '' || next[f] == null) && fullLead[f] != null && fullLead[f] !== '') {
          next[f] = fullLead[f];
        }
      }
      // assigned_sales_id with legacy fallback
      if (!next.assigned_sales_id) {
        next.assigned_sales_id = fullLead.assigned_sales_id || fullLead.assigned_sales_rep_id || '';
      }
      // email re-sanitize from full doc if thin had it blank
      if (!next.email && fullLead.email) {
        next.email = String(fullLead.email).replace(/[\s\u00A0\u200B\u200C\u200D\uFEFF]+/g, '').toLowerCase();
      }
      return next;
    });
  }, [fullLead, lead]);
  const [saving, setSaving] = useState(false);
  const [duplicateError, setDuplicateError] = useState(null);

  // V32.2 — Strip invisible characters that WhatsApp/Excel pastes drag along
  // with copied text. Native HTML5 `type="email"` validator rejects these
  // with the confusing message "A part followed by '@' should not contain the
  // symbol ''" (notice the empty quotes — that's the invisible char). We strip:
  //   \s      → all standard whitespace (space, tab, newline, CR)
  //   \u00A0  → non-breaking space
  //   \u200B  → zero-width space
  //   \u200C  → zero-width non-joiner
  //   \u200D  → zero-width joiner
  //   \uFEFF  → byte-order mark
  // Applied on input AND on submit as a belt-and-braces safety net.
  const sanitizeEmail = (s) => (s || '').replace(/[\s\u00A0\u200B\u200C\u200D\uFEFF]+/g, '').toLowerCase();
  const sanitizeText = (s) => (s || '').replace(/[\u00A0\u200B\u200C\u200D\uFEFF]+/g, '').trim();
  const [doctors, setDoctors] = useState([]);
  const [allLeads, setAllLeads] = useState([]);
  const [doctorSearch, setDoctorSearch] = useState('');
  const [referralSearch, setReferralSearch] = useState('');
  const [leadPayments, setLeadPayments] = useState([]);

  // Q4 — Garbhsanskar Full upgrade eligibility flags
  const hasAddonTrial = leadPayments.some(
    (p) => p.payment_status === 'paid' && p.upsell_opted === true,
  );
  const hasFullAlready = leadPayments.some(
    (p) =>
      p.product_type === 'garbhsanskar_full' &&
      ['paid', 'captured', 'created'].includes(p.payment_status),
  );
  const showGarbhFullUpgrade = hasAddonTrial && !hasFullAlready;

  const handleCreateGarbhFullUpgradeLink = async () => {
    if (!lead?.lead_id) return;
    try {
      const dueDate = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split('T')[0];
      const res = await axios.post(
        `${API_URL}/api/payments/create-link`,
        {
          lead_id: lead.lead_id,
          currency: 'INR',
          product_type: 'garbhsanskar_full',
          due_date: dueDate,
        },
        { withCredentials: true },
      );
      const shortUrl = res.data?.razorpay_short_url;
      if (shortUrl) {
        try {
          await navigator.clipboard.writeText(shortUrl);
          toast.success('Upgrade payment link created & copied to clipboard');
        } catch {
          toast.success('Upgrade payment link created');
          window.prompt('Copy the upgrade payment link:', shortUrl);
        }
      } else {
        toast.success('Upgrade payment link created');
      }
      try {
        const r = await axios.get(
          `${API_URL}/api/leads/${lead.lead_id}/payments`,
          { withCredentials: true },
        );
        setLeadPayments(r.data);
      } catch {
        /* ignore */
      }
    } catch (err) {
      toast.error(
        err.response?.data?.detail || 'Failed to create upgrade payment link',
      );
    }
  };

  useEffect(() => {
    const fetchDoctors = async () => {
      try { const r = await axios.get(`${API_URL}/api/doctors`); setDoctors(r.data); } catch (e) {}
    };
    const fetchAllLeads = async () => {
      try { const r = await axios.get(`${API_URL}/api/leads/all`); setAllLeads(r.data.filter(l => l.lead_id !== lead?.lead_id)); } catch (e) {}
    };
    const fetchPayments = async () => {
      if (!lead?.lead_id) return;
      try { const r = await axios.get(`${API_URL}/api/leads/${lead.lead_id}/payments`, { withCredentials: true }); setLeadPayments(r.data); } catch (e) {}
    };
    fetchDoctors();
    fetchAllLeads();
    fetchPayments();
  }, [lead]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setDuplicateError(null);
    const submitData = {
      ...formData,
      // V32.2 — defensive final sanitization on the way out to the API.
      full_name: sanitizeText(formData.full_name),
      email: sanitizeEmail(formData.email),
      phone: sanitizeText(formData.phone),
    };
    if (submitData.funnel_type === 'Performance Marketing') submitData.funnel_sub_source = '';
    if (submitData.funnel_type !== 'Doctor Referral') submitData.doctor_id = '';
    if (submitData.funnel_type !== 'Referral') { submitData.referred_by_user_id = ''; submitData.referred_by_lead_id = ''; }

    try {
      if (lead) {
        const res = await axios.put(`${API_URL}/api/leads/${lead.lead_id}`, submitData);
        // V52 — the trial is booked in the LMS on save. Never let that fail silently: the modal
        // closes right after, so say it here or the rep thinks the class reached her dashboard.
        const bk = res.data?.lms_trial_booking;
        const touchedTrial = !!(submitData.trial_date && submitData.trial_batch_id);
        if (touchedTrial && bk?.ok) {
          toast.success(`Lead updated · class booked in the LMS (${bk.batch_name} ${bk.scheduled_date}${bk.scheduled_time ? ` ${bk.scheduled_time}` : ''})`);
          if (bk.warning?.message) toast.warning(bk.warning.message, { duration: 12000 });
        } else if (touchedTrial && bk && !bk.ok) {
          toast.success('Lead updated');
          toast.error(`Not booked in the LMS — ${bk.message}`, { duration: 15000 });
        } else {
          toast.success('Lead updated successfully');
        }
      } else {
        const res = await axios.post(`${API_URL}/api/leads`, submitData);
        const isExisting = res.data?.re_registration_count > 0 || (res.data?.lead_id && res.data?.updated_by);
        toast.success(isExisting ? 'Existing lead updated (duplicate detected)' : 'Lead created successfully');
      }
      onSave();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to save lead';
      // A duplicate phone/email block is long and actionable — keep it on screen.
      toast.error(msg, error.response?.status === 409 ? { duration: 12000 } : undefined);
      if (error.response?.status === 409) setDuplicateError(msg);
    } finally { setSaving(false); }
  };

  const preSalesUsers = users.filter(u => ['super_admin', 'pre_sales'].includes(u.role) && u.status === 'active');
  const salesUsers = users.filter(u => ['super_admin', 'sales'].includes(u.role) && u.status === 'active');
  const allActiveUsers = users.filter(u => u.status === 'active');
  const filteredDoctors = doctors.filter(d => d.status === 'active');
  const filteredReferralUsers = allActiveUsers.filter(u => u.full_name?.toLowerCase().includes(referralSearch.toLowerCase()));
  const filteredReferralLeads = allLeads.filter(l => l.full_name?.toLowerCase().includes(referralSearch.toLowerCase()));
  const socialMediaOptions = ['Instagram', 'Facebook', 'WhatsApp', 'YouTube', 'LinkedIn'];

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h2 className="text-xl font-bold text-slate-900" data-testid="lead-modal-title">{readOnly ? 'View Lead' : (lead ? 'Edit Lead' : 'Add New Lead')}</h2>
          {readOnly && <span className="ml-3 mr-auto text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600" data-testid="lead-modal-readonly-badge">Read-only</span>}
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-md"><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <form onSubmit={readOnly ? (e) => e.preventDefault() : handleSubmit} className="p-6 space-y-6">
        <fieldset disabled={readOnly} className="space-y-6 min-w-0" data-testid="lead-modal-fieldset">
        {duplicateError && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800" data-testid="lead-duplicate-error">
            {duplicateError}
          </div>
        )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Full Name *</label>
              <input type="text" required value={formData.full_name} onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} className="input-base" data-testid="lead-name-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
              <input type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: sanitizeEmail(e.target.value) })} className="input-base" data-testid="lead-email-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Phone *</label>
              <input type="tel" required value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="input-base" data-testid="lead-phone-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">City</label>
              <input type="text" value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} className="input-base" placeholder="e.g., Mumbai, Delhi" data-testid="lead-city-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Country</label>
              <select value={formData.country} onChange={(e) => setFormData({ ...formData, country: e.target.value, timezone: e.target.value !== 'United States' ? '' : formData.timezone })} className="input-base">
                <option value="India">India</option>
                <option value="United States">United States</option>
                <option value="United Kingdom">United Kingdom</option>
                <option value="Canada">Canada</option>
                <option value="Australia">Australia</option>
                <option value="United Arab Emirates">United Arab Emirates</option>
                <option value="Singapore">Singapore</option>
                <option value="Malaysia">Malaysia</option>
                <option value="Germany">Germany</option>
                <option value="France">France</option>
                <option value="Saudi Arabia">Saudi Arabia</option>
                <option value="Qatar">Qatar</option>
                <option value="New Zealand">New Zealand</option>
                <option value="South Africa">South Africa</option>
                <option value="Ireland">Ireland</option>
                <option value="Netherlands">Netherlands</option>
                <option value="Switzerland">Switzerland</option>
                <option value="Japan">Japan</option>
                <option value="South Korea">South Korea</option>
                <option value="Hong Kong">Hong Kong</option>
                <option value="Thailand">Thailand</option>
                <option value="Indonesia">Indonesia</option>
                <option value="Philippines">Philippines</option>
                <option value="International">International</option>
                <option value="Other">Other</option>
                {formData.country && !['India','United States','United Kingdom','Canada','Australia','United Arab Emirates','Singapore','Malaysia','Germany','France','Saudi Arabia','Qatar','New Zealand','South Africa','Ireland','Netherlands','Switzerland','Japan','South Korea','Hong Kong','Thailand','Indonesia','Philippines','International','Other'].includes(formData.country) && (
                  <option value={formData.country}>{formData.country}</option>
                )}
              </select>
            </div>
            {(formData.country === 'United States' || formData.country === 'USA') && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Timezone <span className="text-slate-400 text-xs">(optional)</span></label>
                <select value={formData.timezone} onChange={(e) => setFormData({ ...formData, timezone: e.target.value })} className="input-base" data-testid="timezone-select">
                  <option value="">Select Timezone</option>
                  <option value="EST">Eastern (EST/EDT)</option>
                  <option value="CST">Central (CST/CDT)</option>
                  <option value="MST">Mountain (MST/MDT)</option>
                  <option value="PST">Pacific (PST/PDT)</option>
                  <option value="AKST">Alaska (AKST/AKDT)</option>
                  <option value="HST">Hawaii (HST)</option>
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Expected Due Date <span className="text-xs text-slate-400">(Optional)</span></label>
              <input type="date" value={formData.due_date} onChange={(e) => setFormData({ ...formData, due_date: e.target.value })} className="input-base" data-testid="lead-due-date-input" />
              {(fullLead || lead)?.edd_source && (
                <p className="text-xs text-slate-500 mt-1" data-testid="lead-edd-source">
                  Source: {eddSourceLabel((fullLead || lead).edd_source)} — every payment link picks this date up
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Funnel Type{!canEditFunnelFields && <span className="text-xs text-amber-600 ml-2">(Super Admin only)</span>}</label>
              <select value={formData.funnel_type} onChange={(e) => setFormData({ ...formData, funnel_type: e.target.value, funnel_sub_source: '', doctor_id: '', referred_by_user_id: '', referred_by_lead_id: '' })} className={`input-base ${!canEditFunnelFields ? 'bg-slate-100 cursor-not-allowed' : ''}`} disabled={!canEditFunnelFields}>
                <option value="">Select Type</option>
                <option value="Website">Website</option>
                <option value="App">App</option>
                <option value="Social Media">Social Media</option>
                <option value="Referral">Referral</option>
                <option value="Doctor Referral">Doctor Referral</option>
                <option value="Performance Marketing">Performance Marketing</option>
                <option value="Lead Gen">Lead Gen</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {formData.funnel_type === 'Social Media' && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Social Platform{!canEditFunnelFields && <span className="text-xs text-amber-600 ml-2">(Super Admin only)</span>}</label>
                <select value={formData.funnel_sub_source} onChange={(e) => setFormData({ ...formData, funnel_sub_source: e.target.value })} className={`input-base ${!canEditFunnelFields ? 'bg-slate-100 cursor-not-allowed' : ''}`} disabled={!canEditFunnelFields}>
                  <option value="">Select Platform</option>
                  {socialMediaOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              </div>
            )}

            {formData.funnel_type === 'Referral' && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Referred By{!canEditFunnelFields && <span className="text-xs text-amber-600 ml-2">(Super Admin only)</span>}</label>
                {canEditFunnelFields ? (
                  <div className="space-y-2">
                    <input type="text" value={referralSearch} onChange={(e) => setReferralSearch(e.target.value)} className="input-base" placeholder="Search user or lead..." />
                    {referralSearch && (
                      <div className="border border-slate-200 rounded-md max-h-40 overflow-y-auto">
                        {filteredReferralUsers.length > 0 && (
                          <div>
                            <div className="px-3 py-1 bg-slate-100 text-xs font-medium text-slate-500">Team Members</div>
                            {filteredReferralUsers.slice(0, 5).map(u => (
                              <div key={u.user_id} onClick={() => { setFormData({ ...formData, referred_by_user_id: u.user_id, referred_by_lead_id: '', funnel_sub_source: u.full_name }); setReferralSearch(''); }} className={`px-3 py-2 cursor-pointer hover:bg-slate-50 ${formData.referred_by_user_id === u.user_id ? 'bg-indigo-50' : ''}`}>
                                <div className="text-sm font-medium">{u.full_name}</div><div className="text-xs text-slate-500">{u.email}</div>
                              </div>
                            ))}
                          </div>
                        )}
                        {filteredReferralLeads.length > 0 && (
                          <div>
                            <div className="px-3 py-1 bg-slate-100 text-xs font-medium text-slate-500">Existing Leads</div>
                            {filteredReferralLeads.slice(0, 5).map(l => (
                              <div key={l.lead_id} onClick={() => { setFormData({ ...formData, referred_by_lead_id: l.lead_id, referred_by_user_id: '', funnel_sub_source: l.full_name }); setReferralSearch(''); }} className={`px-3 py-2 cursor-pointer hover:bg-slate-50 ${formData.referred_by_lead_id === l.lead_id ? 'bg-indigo-50' : ''}`}>
                                <div className="text-sm font-medium">{l.full_name}</div><div className="text-xs text-slate-500">{l.email}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    {formData.funnel_sub_source && !referralSearch && <div className="text-sm text-slate-600">Selected: <span className="font-medium">{formData.funnel_sub_source}</span></div>}
                  </div>
                ) : <input type="text" value={formData.funnel_sub_source || ''} className="input-base bg-slate-100 cursor-not-allowed" disabled />}
              </div>
            )}

            {formData.funnel_type === 'Doctor Referral' && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Referring Doctor{!canEditFunnelFields && <span className="text-xs text-amber-600 ml-2">(Super Admin only)</span>}</label>
                {canEditFunnelFields ? (
                  <select
                    value={formData.doctor_id}
                    onChange={(e) => {
                      const doc = filteredDoctors.find(d => d.doctor_id === e.target.value);
                      setFormData({ ...formData, doctor_id: e.target.value, funnel_sub_source: doc?.name || '' });
                    }}
                    className="input-base"
                    data-testid="referring-doctor-select"
                  >
                    <option value="">Select Doctor</option>
                    {filteredDoctors.map(d => (
                      <option key={d.doctor_id} value={d.doctor_id}>{d.name}</option>
                    ))}
                  </select>
                ) : <input type="text" value={formData.funnel_sub_source || ''} className="input-base bg-slate-100 cursor-not-allowed" disabled />}
              </div>
            )}

            {formData.funnel_type === 'Website' && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Landing Page{!canEditFunnelFields && <span className="text-xs text-amber-600 ml-2">(Super Admin only)</span>}</label>
                <input type="text" value={formData.funnel_sub_source} onChange={(e) => setFormData({ ...formData, funnel_sub_source: e.target.value })} className={`input-base ${!canEditFunnelFields ? 'bg-slate-100 cursor-not-allowed' : ''}`} placeholder="e.g., Homepage, Pricing Page" disabled={!canEditFunnelFields} />
              </div>
            )}

            {formData.funnel_type === 'Other' && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Source Details{!canEditFunnelFields && <span className="text-xs text-amber-600 ml-2">(Super Admin only)</span>}</label>
                <input type="text" value={formData.funnel_sub_source} onChange={(e) => setFormData({ ...formData, funnel_sub_source: e.target.value })} className={`input-base ${!canEditFunnelFields ? 'bg-slate-100 cursor-not-allowed' : ''}`} placeholder="Specify source" disabled={!canEditFunnelFields} />
              </div>
            )}

            {formData.funnel_type === 'Performance Marketing' && formData.funnel_sub_source && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">UTM Source <span className="text-xs text-amber-600 ml-2">(Auto-captured)</span></label>
                <input type="text" value={formData.funnel_sub_source} className="input-base bg-slate-100 cursor-not-allowed text-xs" disabled />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Assigned Pre-Sales{!canTransferPreSales && <span className="text-xs text-amber-600 ml-2">(Super Admin only)</span>}</label>
              <select value={formData.assigned_pre_sales_id} onChange={(e) => setFormData({ ...formData, assigned_pre_sales_id: e.target.value })} className={`input-base ${!canTransferPreSales ? 'bg-slate-100 cursor-not-allowed' : ''}`} disabled={!canTransferPreSales} data-testid="lead-assigned-presales">
                <option value="">Select Pre-Sales</option>
                {preSalesUsers.map(u => <option key={u.user_id} value={u.user_id}>{u.full_name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Assigned Sales{!canTransferSales && <span className="text-xs text-amber-600 ml-2">(Team Leader / Super Admin only)</span>}</label>
              <select value={formData.assigned_sales_id} onChange={(e) => setFormData({ ...formData, assigned_sales_id: e.target.value })} className={`input-base ${!canTransferSales ? 'bg-slate-100 cursor-not-allowed' : ''}`} disabled={!canTransferSales} data-testid="lead-assigned-sales">
                <option value="">Select Sales</option>
                {salesUsers.map(u => <option key={u.user_id} value={u.user_id}>{u.full_name}</option>)}
              </select>
              {!lead && <p className="text-xs text-slate-500 mt-1">Leave blank to auto-assign a Pre-Sales owner by round robin. Sales is assigned when a consultation is booked.</p>}
            </div>

            {lead && (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Trial Date</label>
                  <input type="date" value={formData.trial_date} onChange={(e) => setFormData({ ...formData, trial_date: e.target.value })} className="input-base" data-testid="trial-date-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Trial Batch</label>
                  <select value={formData.trial_batch_id} onChange={(e) => setFormData({ ...formData, trial_batch_id: e.target.value, trial_slot_id: '' })} className="input-base" data-testid="trial-batch-select">
                    <option value="">Select Batch</option>
                    {batches.filter(b => b.active_status).map(batch => <option key={batch.batch_id} value={batch.batch_id}>{batch.batch_name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Trial Slot</label>
                  <select value={formData.trial_slot_id} onChange={(e) => setFormData({ ...formData, trial_slot_id: e.target.value })} className="input-base" disabled={!formData.trial_batch_id} data-testid="trial-slot-select">
                    <option value="">Select Slot</option>
                    {formData.trial_batch_id && batches.find(b => b.batch_id === formData.trial_batch_id)?.slots?.filter(s => s.active_status).map(slot => (
                      <option key={slot.slot_id} value={slot.slot_id}>{slot.slot_name} ({slot.slot_time_start} - {slot.slot_time_end})</option>
                    ))}
                  </select>
                  {!formData.trial_batch_id && <p className="text-xs text-slate-500 mt-1">Select a batch first</p>}
                  <LmsSlotHint
                    batchId={formData.trial_batch_id}
                    date={formData.trial_date}
                    slots={batches.find(b => b.batch_id === formData.trial_batch_id)?.slots || []}
                  />
                </div>
                <TrialLmsStatus
                  lead={fullLead}
                  selectedBatchName={batches.find(b => b.batch_id === formData.trial_batch_id)?.batch_name}
                  onRefresh={() => { refreshFullLead(); onRefresh?.(); }}
                />
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Trial Status</label>
                  {(() => {
                    // V32.3 — Trial Status is auto-derived from Date+Batch+Slot
                    // completeness for non-terminal states. Show as locked when
                    // pending/unpaid/scheduled (system-managed), unlock only when
                    // the user explicitly needs to mark the trial as Completed,
                    // No Show, or Cancelled post-event.
                    const cur = formData.trial_status;
                    const isTerminal = ['completed', 'no_show', 'cancelled'].includes(cur);
                    const allSlotFieldsFilled = !!(formData.trial_date && formData.trial_batch_id && formData.trial_slot_id);
                    // Allow editing when: status is already terminal, OR when the
                    // trial has reached "scheduled" (so agent can post-mark it).
                    const canEdit = isTerminal || cur === 'scheduled' || allSlotFieldsFilled;
                    return (
                      <>
                        <select
                          value={cur === 'unpaid' ? 'pending' : cur}
                          onChange={(e) => setFormData({ ...formData, trial_status: e.target.value })}
                          className={`input-base ${!canEdit ? 'bg-slate-50 text-slate-500 cursor-not-allowed' : ''}`}
                          disabled={!canEdit}
                          data-testid="trial-status-select"
                        >
                          <option value="pending">Pending</option>
                          <option value="scheduled">Scheduled</option>
                          <option value="completed">Completed</option>
                          <option value="no_show">No Show</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                        <p className="text-xs text-slate-500 mt-1">
                          {canEdit
                            ? 'Mark Completed / No Show / Cancelled after the trial happens.'
                            : 'Auto-set from Trial Date + Batch + Slot. Fill all three to schedule.'}
                        </p>
                      </>
                    );
                  })()}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Trial Payment</label>
                  <select
                    value={formData.trial_payment_status}
                    onChange={(e) => setFormData({ ...formData, trial_payment_status: e.target.value })}
                    className={`input-base ${lead?.trial_payment_status === 'paid' ? 'bg-green-50 text-green-700 cursor-not-allowed' : ''}`}
                    disabled={lead?.trial_payment_status === 'paid'}
                    data-testid="trial-payment-status-select"
                  >
                    <option value="not_paid">Not Paid</option>
                    <option value="paid">Paid</option>
                  </select>
                  {lead?.trial_payment_status === 'paid' && <p className="text-xs text-green-600 mt-1">Payment captured — locked</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Consultation Status</label>
                  <select value={formData.consultation_status} onChange={(e) => setFormData({ ...formData, consultation_status: e.target.value })} className="input-base">
                    <option value="pending">Pending</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="completed">Completed</option>
                    <option value="no_show">No Show</option>
                    <option value="not_interested">Not Interested</option>
                  </select>
                  {lead?.consultation_booking_date && (
                    <div className="mt-2 p-2.5 bg-indigo-50 border border-indigo-200 rounded-lg" data-testid="consultation-datetime">
                      <p className="text-xs font-semibold text-indigo-700 mb-1">{lead.consultation_event_name || 'Consultation'}</p>
                      <p className="text-sm font-medium text-indigo-900">
                        {new Date(lead.consultation_booking_date + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}
                        {' at '}
                        {(() => { const [h,m] = (lead.consultation_booking_time || '').split(':'); const d = new Date(); d.setHours(parseInt(h)||0, parseInt(m)||0); return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }); })()}
                        {' '}
                        <span className="text-xs text-indigo-600">IST</span>
                      </p>
                      {lead.consultation_user_timezone && lead.consultation_user_timezone !== (lead.consultation_booking_timezone || 'Asia/Kolkata') && (
                        <p className="text-xs text-indigo-600 mt-0.5">
                          Customer TZ: {lead.consultation_user_timezone}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}

            {lead && (
              <div data-testid="lead-score-section">
                <label className="block text-sm font-medium text-slate-700 mb-1">Lead Score <span className="text-xs text-slate-400">(from LMS, read-only)</span></label>
                <div className="input-base bg-slate-50 flex items-center justify-between">
                  <span className={`inline-flex items-center gap-1 font-semibold ${fullLead?.is_hot ? 'text-orange-600' : 'text-slate-700'}`} data-testid="lead-score-value">
                    {fullLead?.is_hot && <Flame className="w-4 h-4" />}
                    {typeof fullLead?.lead_score === 'number' ? fullLead.lead_score : '—'}
                  </span>
                  {fullLead?.lead_score_at && <span className="text-xs text-slate-400">{new Date(fullLead.lead_score_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</span>}
                </div>
                {(fullLead?.lead_score_reasons || []).length > 0 && (
                  <p className="text-xs text-slate-500 mt-1" data-testid="lead-score-reasons">{fullLead.lead_score_reasons.join(' · ')}</p>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Lead Quality</label>
              <select value={formData.lead_quality} onChange={(e) => setFormData({ ...formData, lead_quality: e.target.value })} className="input-base" data-testid="lead-quality-select">
                <option value="not_rated">Not Rated</option>
                <option value="good">Good</option>
                <option value="average">Average</option>
                <option value="bad">Bad</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Lead Status</label>
              <select value={formData.lead_status} onChange={(e) => setFormData({ ...formData, lead_status: e.target.value })} className="input-base" data-testid="lead-status-modal-select">
                <option value="new">New</option>
                <option value="dnp">DNP (Did Not Pick)</option>
                <option value="callback_later">Callback Later</option>
                <option value="future_prospect">Future Prospect</option>
                <option value="not_interested">Not Interested</option>
                <option value="number_not_in_service">Number Not in Service</option>
                <option value="wip">WIP (Work In Progress)</option>
                <option value="not_eligible">Not Eligible</option>
                <option value="enrolled">Enrolled</option>
                <option value="no_show">No Show</option>
              </select>
            </div>

            {formData.lead_status === 'callback_later' && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Callback Scheduled <span className="text-xs text-amber-600 ml-2">(Reminder 60 mins before)</span></label>
                <input type="datetime-local" value={formData.callback_datetime} onChange={(e) => setFormData({ ...formData, callback_datetime: e.target.value })} className="input-base" min={new Date().toISOString().slice(0, 16)} data-testid="callback-datetime-modal-input" />
                {formData.callback_datetime && (
                  <p className="text-xs text-slate-500 mt-1">Scheduled: {new Date(formData.callback_datetime).toLocaleString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                )}
              </div>
            )}

            {lead?.custom_fields && Object.keys(lead.custom_fields).length > 0 && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">Booking Answers</label>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-1" data-testid="lead-custom-fields">
                  {Object.entries(lead.custom_fields).map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-3 text-sm">
                      <span className="text-slate-500 capitalize">{k.replace(/_/g, ' ')}</span>
                      <span className="text-slate-800 font-medium text-right">{String(v)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {lead && (
              <>
                <AlternatePhones lead={fullLead || lead} onChanged={(merged) => { if (merged) onSave(); else refreshFullLead(); }} />
                <OwnershipHistory history={(fullLead || lead)?.ownership_history} />
                <EddHistory lead={fullLead || lead} />
              </>
            )}
            {lead && (
              <>
                <AddonStatus lead={fullLead || lead} />
                <CountrySignals lead={fullLead || lead} onChanged={async () => { await refreshFullLead(); onRefresh?.(); }} />
              </>
            )}

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">Pregnancy Notes</label>
              <textarea value={formData.pregnancy_notes} onChange={(e) => setFormData({ ...formData, pregnancy_notes: e.target.value })} className="input-base min-h-[80px]" placeholder="Notes about pregnancy details, due date, concerns, etc." data-testid="pregnancy-notes" />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">Sales Notes</label>
              <textarea value={formData.sales_notes} onChange={(e) => setFormData({ ...formData, sales_notes: e.target.value })} className="input-base min-h-[80px]" placeholder="Notes from sales calls, follow-ups, objections, etc." data-testid="sales-notes" />
            </div>

            {lead?.lead_id && <RenewalStatusField leadId={lead.lead_id} />}

            {lead?.payment_locked && (
              <div className="md:col-span-2 bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <LockIcon className="w-4 h-4 text-emerald-600" />
                  <h4 className="font-semibold text-emerald-800 text-sm">Enrolled - Payment Locked</h4>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><span className="text-slate-500">Course:</span><p className="font-medium text-slate-900">{lead.enrolled_course || 'N/A'}</p></div>
                  <div><span className="text-slate-500">Amount Paid:</span><p className="font-medium text-emerald-700">{lead.enrolled_amount?.toLocaleString('en-IN') || '0'}</p></div>
                  <div><span className="text-slate-500">Tenure:</span><p className="font-medium text-slate-900">{lead.enrolled_tenure_months || 0} months</p></div>
                  <div><span className="text-slate-500">Tenure Ends:</span><p className="font-medium text-slate-900">{lead.enrolled_tenure_end_date ? new Date(lead.enrolled_tenure_end_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}</p></div>
                  <div className="col-span-2"><span className="text-slate-500">Enrolled On:</span><p className="font-medium text-slate-900">{lead.enrolled_at ? new Date(lead.enrolled_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A'}</p></div>
                </div>
                <p className="text-xs text-emerald-600 mt-3 italic">This information is locked and cannot be edited. Captured automatically from payment.</p>
              </div>
            )}

            {/* Q4 — Garbhsanskar Full upgrade button. Shown when lead has paid trial
                with upsell (₹199 addon) but doesn't yet have a full-course payment. */}
            {lead && showGarbhFullUpgrade && (
              <div className="md:col-span-2 bg-gradient-to-r from-indigo-50 to-rose-50 border border-indigo-200 rounded-lg p-4" data-testid="garbhsanskar-full-upgrade-block">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-indigo-600 flex items-center justify-center flex-shrink-0">
                    <CreditCard className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-slate-900 text-sm">Upgrade to Garbhsanskar Full Course</h4>
                    <p className="text-xs text-slate-600 mt-1">
                      This lead has the ₹199 add-on. Unlock all 9 episodes with a single upgrade payment link.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleCreateGarbhFullUpgradeLink}
                    className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 whitespace-nowrap"
                    data-testid="create-garbhsanskar-full-upgrade-btn"
                  >
                    Create ₹1,499 link
                  </button>
                </div>
              </div>
            )}

            {/* Payment History */}
            {lead && leadPayments.length > 0 && (
              <div className="md:col-span-2 bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid="payment-history-section">
                <div className="flex items-center gap-2 mb-3">
                  <CreditCard className="w-4 h-4 text-slate-600" />
                  <h4 className="font-semibold text-slate-800 text-sm">Payment History ({leadPayments.length})</h4>
                </div>
                <div className="space-y-3">
                  {leadPayments.map((p) => {
                    const label = p.payment_type === 'refund' ? 'Refund'
                      : p.product === 'diet_chart' || p.product_type === 'diet_plan' ? 'Diet Plan'
                      : p.product === 'soulai_coach' || p.product_type?.startsWith('soulai_coach') ? 'SoulAI Coach'
                      : p.product === 'baby_tracker' || p.product_type?.startsWith('baby_tracker') ? 'Baby Tracker'
                      : p.payment_type === 'trial' ? 'Trial Class'
                      : (p.product_type === 'Trial Class' || p.product_type === 'US Trial Class') ? 'Trial Class'
                      : p.tenure_months > 1 || p.is_till_delivery ? 'Enrollment'
                      : p.product_type?.toLowerCase().includes('renewal') ? 'Renewal'
                      : 'Enrollment';
                    const isPaid = p.payment_status === 'paid';
                    return (
                      <div key={p.payment_id} className={`border rounded-lg p-3 ${isPaid ? 'bg-white border-emerald-200' : 'bg-white border-slate-200'}`} data-testid={`payment-card-${p.payment_id}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                            label === 'Trial Class' ? 'bg-blue-100 text-blue-700'
                            : label === 'Refund' ? 'bg-rose-100 text-rose-700'
                            : label === 'SoulAI Coach' || label === 'Baby Tracker' ? 'bg-teal-100 text-teal-700'
                            : label === 'Diet Plan' ? 'bg-emerald-100 text-emerald-700'
                            : label === 'Renewal' ? 'bg-amber-100 text-amber-700'
                            : 'bg-purple-100 text-purple-700'
                          }`}>{label}</span>
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                            isPaid ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                          }`}>{isPaid ? 'Paid' : p.payment_status === 'created' ? 'Pending' : (p.payment_status || '').replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase())}</span>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          <div>
                            <span className="text-slate-400">{p.access_label ? 'Access' : 'Amount'}</span>
                            {p.access_label ? (
                              <p className="font-semibold text-teal-700" data-testid={`access-label-${p.payment_id}`}>
                                {p.access_label}
                                {p.access_expires_at ? <span className="block text-xs font-normal text-slate-500">till {String(p.access_expires_at).slice(0, 10)}</span> : null}
                              </p>
                            ) : (
                              <p className={`font-semibold ${isPaid ? 'text-emerald-700' : 'text-slate-700'}`}>
                                {p.currency === 'USD' ? '$' : '₹'}{p.total_amount?.toLocaleString('en-IN')}
                              </p>
                            )}
                          </div>
                          <div className={p.access_label ? 'hidden' : ''}>
                            <span className="text-slate-400">{p.gst_amount > 0 ? 'Base + GST' : 'Base'}</span>
                            <p className="text-slate-700">{p.currency === 'USD' ? '$' : '₹'}{p.base_amount?.toLocaleString('en-IN')}{p.gst_amount > 0 ? ` + ${p.currency === 'USD' ? '$' : '₹'}${p.gst_amount?.toLocaleString('en-IN')}` : ''}</p>
                          </div>
                          <div>
                            <span className="text-slate-400">Date</span>
                            <p className="text-slate-700">{p.captured_at ? new Date(p.captured_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : p.created_at ? new Date(p.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}</p>
                          </div>
                          {p.tenure_months && (
                            <div>
                              <span className="text-slate-400">Tenure</span>
                              <p className="text-slate-700">{p.is_till_delivery ? 'Till Delivery' : `${p.tenure_months} month${p.tenure_months > 1 ? 's' : ''}`}</p>
                            </div>
                          )}
                          {p.discount_amount > 0 && (
                            <div>
                              <span className="text-slate-400">Discount</span>
                              <p className="text-green-600">-{p.currency === 'USD' ? '$' : '₹'}{p.discount_amount?.toLocaleString('en-IN')}</p>
                            </div>
                          )}
                          {p.refund_amount > 0 && (
                            <div className="col-span-3 mt-1 pt-1 border-t border-rose-100" data-testid={`payment-refund-${p.payment_id}`}>
                              <span className="text-slate-400">Refund</span>
                              <p className="text-rose-700 font-medium">
                                -{p.currency === 'USD' ? '$' : '₹'}{Math.abs(p.refund_amount)?.toLocaleString('en-IN')}
                                {p.refund_date ? ` · ${new Date(p.refund_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}` : ''}
                                {p.refund_request_id ? ' · learner request' : ''}{p.razorpay_refund_id ? ` · ${p.razorpay_refund_id}` : ''}
                              </p>
                              {p.refund_reason && <p className="text-slate-500">{p.refund_reason}</p>}
                            </div>
                          )}
                          {p.addons_detail?.length > 0 && (
                            <div className="col-span-3 mt-1 pt-2 border-t border-slate-100" data-testid={`payment-addons-${p.payment_id}`}>
                              <span className="text-slate-400">Add-ons</span>
                              <div className="flex flex-wrap gap-1.5 mt-1">
                                {p.addons_detail.map((a) => (
                                  <span key={a.product}
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-teal-50 border border-teal-200 text-teal-800 text-[11px]"
                                    data-testid={`payment-addon-${p.payment_id}-${a.product}`}>
                                    <span className="font-medium">{a.label}</span>
                                    <span className="text-teal-600">·</span>
                                    <span className={a.complimentary ? 'italic' : ''}>{a.amount_label}</span>
                                    {a.note ? <span className="text-teal-600/80">· {a.note}</span> : null}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {p.product_type && (
                            <div>
                              <span className="text-slate-400">Product</span>
                              <p className="text-slate-700">{p.display_name || p.product_type}</p>
                            </div>
                          )}
                          {p.is_free_coupon_order && (
                            <div className="col-span-3" data-testid={`payment-free-coupon-${p.payment_id}`}>
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-[11px]">
                                <span className="font-medium">Free (coupon)</span>
                                {p.coupon_code ? <span>· {p.coupon_code}</span> : null}
                                <span>· ₹0 enrolment, not revenue</span>
                                {p.access_until ? <span>· access until {String(p.access_until).slice(0, 10)}</span> : null}
                              </span>
                            </div>
                          )}
                          {(p.source === 'lms_dietician' || p.category === 'Diet Payment') && (
                            <div className="col-span-3" data-testid={`payment-diet-${p.payment_id}`}>
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-[11px]">
                                <span className="font-medium">Diet Plan payment</span>
                                {p.description ? <span data-testid={`payment-diet-desc-${p.payment_id}`}>· {p.description}</span> : null}
                                {p.dietician_name ? <span>· raised by {p.dietician_name} in the LMS</span> : <span>· raised in the LMS</span>}
                                {p.diet_plan?.country ? <span>· {p.diet_plan.country}</span> : null}
                              </span>
                            </div>
                          )}
                          {p.payment_created_by_name && (
                            <div>
                              <span className="text-slate-400">Generated By</span>
                              <p className="text-slate-700 font-medium" data-testid={`payment-creator-${p.payment_id}`}>{p.payment_created_by_name}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Re-registration History (non-editable) */}
            {lead?.re_registrations?.length > 0 && (
              <div className="md:col-span-2 bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="re-registration-section">
                <div className="flex items-center gap-2 mb-3">
                  <RefreshCw className="w-4 h-4 text-amber-600" />
                  <h4 className="font-semibold text-amber-800 text-sm">Duplicate Submissions ({lead.re_registrations.length})</h4>
                </div>
                <div className="space-y-2">
                  {lead.re_registrations.map((r, idx) => (
                    <div key={idx} className="bg-white border border-amber-100 rounded-md px-3 py-2 text-xs text-slate-700">
                      <p>{r.note}</p>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-amber-500 mt-2 italic">Auto-logged on duplicate lead creation. Read-only.</p>
              </div>
            )}

            {lead && (lead.utm_source || lead.utm_campaign || lead.fbclid || lead.fbc || lead.fbp || lead.landing_page_url || lead.ai_engine) && (
              <div className="md:col-span-2 bg-indigo-50 border border-indigo-200 rounded-lg p-4" data-testid="attribution-section">
                <h4 className="font-semibold text-indigo-800 mb-3 text-sm">Attribution & Tracking</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                  {lead.ai_engine && <div data-testid="attr-ai-engine"><span className="text-slate-500 text-xs">Found us via</span><p className="font-semibold text-indigo-800">{AI_ENGINE_LABELS[lead.ai_engine] || lead.ai_engine} <span className="font-normal text-slate-500 text-xs">· organic</span></p></div>}
                  {lead.utm_source && <div><span className="text-slate-500 text-xs">UTM Source</span><p className="font-medium text-slate-900">{lead.utm_source}</p></div>}
                  {lead.utm_medium && <div><span className="text-slate-500 text-xs">UTM Medium</span><p className="font-medium text-slate-900">{lead.utm_medium}</p></div>}
                  {lead.utm_campaign && <div><span className="text-slate-500 text-xs">UTM Campaign</span><p className="font-medium text-slate-900">{lead.utm_campaign}</p></div>}
                  {lead.utm_content && <div><span className="text-slate-500 text-xs">UTM Content</span><p className="font-medium text-slate-900">{lead.utm_content}</p></div>}
                  {lead.utm_term && <div><span className="text-slate-500 text-xs">UTM Term</span><p className="font-medium text-slate-900">{lead.utm_term}</p></div>}
                  {lead.fbclid && <div><span className="text-slate-500 text-xs">FB Click ID</span><p className="font-mono text-xs text-slate-700 truncate">{lead.fbclid}</p></div>}
                  {lead.fbc && <div><span className="text-slate-500 text-xs">FBC Cookie</span><p className="font-mono text-xs text-slate-700 truncate">{lead.fbc}</p></div>}
                  {lead.fbp && <div><span className="text-slate-500 text-xs">FBP Cookie</span><p className="font-mono text-xs text-slate-700 truncate">{lead.fbp}</p></div>}
                  {lead.landing_page_url && !lead.ai_engine && <div className="col-span-2 md:col-span-3"><span className="text-slate-500 text-xs">Landing Page</span><p className="font-medium text-slate-900">{lead.landing_page_url}</p></div>}
                </div>
                <p className="text-xs text-indigo-500 mt-2 italic">Auto-captured from website. Read-only.</p>
              </div>
            )}
          </div>
        </fieldset>
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
            <button type="button" onClick={onClose} className="btn-secondary" data-testid="lead-modal-close-btn">{readOnly ? 'Close' : 'Cancel'}</button>
            {!readOnly && <button type="submit" disabled={saving} className="btn-primary" data-testid="save-lead-btn">{saving ? 'Saving...' : (lead ? 'Update Lead' : 'Create Lead')}</button>}
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default LeadModal;
